import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EditSectionTitleComponent } from '@modeso/aumico-lib-core-fe';
import { IStyleOptions } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, tap } from 'rxjs';
import {
    AnnualReportProvider,
    FixedAssetScheduleRowFormatted,
    ISectionData,
    MarkerClasses,
    PrintUsingPdfTronDto,
    RowTypes,
} from '../../..';

@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-fixed-assets-schedule-swiss-display-component',
    templateUrl: 'fixed-assets-schedule-swiss-display.component.html',
    styleUrls: ['fixed-assets-schedule-swiss-display.component.scss'],
})
export class FixedAssetsScheduleSwissDisplayComponent implements OnInit {
    header: FixedAssetScheduleRowFormatted[] = [];
    data: FixedAssetScheduleRowFormatted[] = [];
    textColor: string;
    backgroundColor: string;
    tableFooterColor: string;
    tableHeaderColor: string;
    sectionTitle: string;
    // Hides the previous book value column.
    noPrevious = false;
    @Input() set stylePreferenceOptions(value: IStyleOptions) {
        if (value != null) {
            this.textColor = value.textColor;
            this.backgroundColor = value.backgroundColor;
            this.tableHeaderColor = value.tableHeaderColor;
            this.tableFooterColor = value.tableFooterColor;
        }
    }

    @Input() markerClass: MarkerClasses;

    @Output() sectionTitleChanged: EventEmitter<string> = new EventEmitter<string>();

    @HostBinding('style.--fas-style-1') style1 = 'upper-alpha';
    @HostBinding('style.--fas-style-2') style2 = 'upper-roman';
    @HostBinding('style.--fas-style-3') style3 = 'decimal';
    @HostBinding('style.--fas-style-4') style4 = 'lower-alpha';
    @HostBinding('style.--fas-style-5') style5 = 'none';
    @HostBinding('style.--fas-style-6') style6 = 'none';
    @HostBinding('style.--fas-style-7') style7 = 'none';
    @HostBinding('style.--fas-style-8') style8 = 'none';
    @HostBinding('style.--fas-style-9') style9 = 'none';
    @HostBinding('style.--fas-style-10') style10 = 'none';

    constructor(
        private readonly annualReportProvider: AnnualReportProvider,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private dialog: MatDialog,
    ) {
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    ngOnInit(): void {
        this.annualReportProvider
            .getPrintView$()
            .pipe(
                filter(
                    (printViewData: PrintUsingPdfTronDto) => printViewData != null && printViewData.data.fas != null,
                ),
                map((printViewData: PrintUsingPdfTronDto) => printViewData.data.fas),
                tap((fasSection: ISectionData) => {
                    this.sectionTitle = fasSection.title;
                    this.noPrevious = fasSection.noPrevious === true;
                }),
                map((fasSection: ISectionData) => {
                    return fasSection.data as FixedAssetScheduleRowFormatted[];
                }),
                untilDestroyed(this),
            )
            .subscribe((fasTableData) => {
                this.header = fasTableData.filter((row) => row.rowType == RowTypes.TABLE_HEAD);
                this.data = fasTableData.filter((row) => row.rowType !== RowTypes.TABLE_HEAD);
            });
    }

    public editSectionTitle(): void {
        const dialogRef = this.dialog.open<EditSectionTitleComponent, string, string | undefined>(
            EditSectionTitleComponent,
            {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: this.sectionTitle,
            },
        );

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result != null) {
                    this.sectionTitle = result;
                    this.sectionTitleChanged.emit(result);
                }
            });
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
